import { PDFViewer, usePDF } from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import CustomButton from "../../Components/Ui/CustomButton";
import ToggleButton from "../../Components/Ui/ToggleButton/ToggleButton";
import { useAuth } from "../../Store/auth-context";
import { Product } from "../../Store/interfaces";
import { useScheinContext } from "../../Store/schein-context";
import { useUIContext } from "../../Store/ui-context";
import { postServiceschein } from "../../Utils/api";
import PDFServiceschein from "./PdfServiceschein";

// Define props for FinalPage component
interface FinalPageProps {
  onButtonClick: (page: string) => void;
}

const FinalPage: React.FC<FinalPageProps> = ({ onButtonClick }) => {
  const [showPdfViewer, setShowPdfViewer] = useState<boolean>(false);
  const [servicescheinSend, setServicescheinSend] = useState<boolean>(false);
  const [showBackButton, setShowBackButton] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pdfbase64, setPdfbase64] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [isAutomaticInvoice, setisAutomaticInvoice] = useState(true);
  const [customEmail4RE, setCustomEmail4RE] = useState("");
  const [selectedeSeSEmail, setSelectedeSeSEmail] = useState("");
  const [selectedREEmail, setSelectedREEmail] = useState("");
  const [customServicescheinEmail, setCustomServicescheinEmail] = useState("");

  const [kontrollsumme, setKontrollsumme] = useState(0);
  const [isREEmailValid, setIsREEmailValid] = useState(false);
  const [isServicescheinEmailValid, setIsServicescheinEmailValid] =
    useState(false);

  const {
    technicianSignature,
    customerSignature,
    day,
    startTime,
    endTime,
    comment,
    selectedProducts,
    selectedCustomer,
    zielOrt,
    startOrt,
    distanz,
    fahrtZeit,
    fahrtBerechnen,
    setComment,
    setSelectedProducts,
    setZielOrt,
    setStartOrt,
    setDistanz,
    setFahrtZeit,
    setPdf,
    pdf,
  } = useScheinContext();

  useEffect(() => {
    const emails = selectedCustomer.email;
    if (emails && emails.length > 0) {
      setSelectedeSeSEmail(emails[0]);
      setSelectedREEmail(emails[0]);
    } else {
      setSelectedeSeSEmail("");
      setSelectedREEmail("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  const { vorname, nachname } = useAuth();
  const { showDialog } = useUIContext();

  const [instance, update] = usePDF({
    document: (
      <PDFServiceschein
        selectedCustomer={selectedCustomer}
        customerSign={customerSignature}
        techSign={technicianSignature}
        vorname={vorname}
        nachname={nachname}
        startTime={startTime}
        endTime={endTime}
        comment={comment}
        products={selectedProducts}
        zielOrt={zielOrt}
        startOrt={startOrt}
        distanz={distanz}
        fahrtZeit={fahrtZeit}
        fahrtBerechnen={fahrtBerechnen}
        day={day}
      />
    ),
  });

  const [isLoading, setIsLoading] = useState(false);

  const datum = `${day.getDate().toString().padStart(2, "0")}.${(
    day.getUTCMonth() + 1
  )
    .toString()
    .padStart(2, "0")}.${day.getFullYear()}`;

  const sTime = `${startTime.getHours().toString().padStart(2, "0")}:${startTime
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const eTime = `${endTime.getHours().toString().padStart(2, "0")}:${endTime
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  // infotext to write infront of the comment in the position
  const preText: string = `Durchgeführt von ${vorname.substring(
    0,
    1
  )}. ${nachname} am ${datum} in der Zeit von ${sTime} - ${eTime}`;

  // try fixing no PDF generation error
  useEffect(() => {
    generatePdfDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to convert blob to base64
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      try {
        setIsLoading(true);
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result?.toString() || "";
          resolve(base64data);
        };
        reader.onerror = reject;
      } catch (error) {
        alert("Fehler beim Erzeugen des PDFs. Bitte erneut versuchen.");
      } finally {
        setIsLoading(false);
      }
    });
  };

  // useEffect for calculating the checksum
  useEffect(() => {
    var summe = 0;
    selectedProducts.forEach((product) => {
      summe = summe + Number(product.vk) * Number(product.menge);

      if (product.artikel.includes("(Datum)")) {
        product.artikel = product.artikel.substring(
          0,
          product.artikel.indexOf("(Datum)")
        );
      }
    });
    // summe = 11000;
    setKontrollsumme(summe);
  }, [selectedProducts]);

  const handleSendServiceSchein = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (servicescheinSend || loading) return;

    await generatePdfDocument();

    if (
      !instance ||
      !instance.blob ||
      instance.blob.size <= 0 ||
      pdf.length < 0
    ) {
      setErrorMsg("PDF konnte nicht erstellt werden. Bitte erneut versuchen.");
      return;
    }

    const blob2B64String = await blobToBase64(instance.blob);
    const result = blob2B64String.replace("data:application/pdf;base64,", "");
    setPdf(result);
    setPdfbase64(result);

    showDialog({
      title: "Bestätigung",
      message: "Möchten Sie den Serviceschein wirklich versenden?",
      confirmButtonText: "Versenden",
      cancelButtonText: "Abbrechen",
      onConfirm: async () => {
        try {
          setLoading(true);
          await sendToDatabase(result);
        } catch (error) {
          setErrorMsg("Fehler beim Versenden des Servicescheins: " + error);
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        return;
      },
    });
  };

  const sendToDatabase = async (base64pdf: string) => {
    if (!base64pdf) {
      setErrorMsg("Die PDF fehlt. Bitte erneut erzeugen.");
      return;
    }
    if (!selectedCustomer) {
      setErrorMsg("Der ausgewählte Kunde fehlt oder ist fehlerhaft.");
      return;
    }
    if (!selectedProducts.length) {
      setErrorMsg(
        "Es wurden keine Produkte/Dienstleistungen ausgewählt. Bitte überprüfen."
      );
      return;
    }
    if (!startOrt) {
      setErrorMsg("Der Startort fehlt oder ist fehlerhaft. Bitte überprüfen.");
      return;
    }
    if (!zielOrt) {
      setErrorMsg("Der Zielort fehlt oder ist fehlerhaft. Bitte überprüfen.");
      return;
    }

    const productsArray: Product[] = [];

    let specialHandled = false;

    selectedProducts.forEach((prod) => {
      if (
        prod.id === 186 ||
        prod.id === 1394 ||
        prod.id === 1396 ||
        prod.id === 1397
      ) {
        specialHandled = true;

        if (!prod.kommentar || !prod.kommentar.includes(comment)) {
          prod.kommentar = prod.kommentar
            ? `${preText} ${prod.kommentar} -- ${comment}`
            : preText + "\n" + comment;
        }
      } else if (!specialHandled && prod.artikel?.includes("Arbeitsstunden")) {
        prod.kommentar = prod.kommentar || "";
        prod.beschreibung = "";

        if (!prod.kommentar.includes(comment)) {
          if (prod.artikel !== "Arbeitsstunden") {
            prod.artikel = "Arbeitsstunden";
          }
          prod.kommentar = prod.kommentar
            ? `${preText} ${prod.kommentar} -- ${comment}`
            : preText + "\n" + comment;

          specialHandled = true; // Nur eine Position soll bearbeitet werden
        }
      }
      productsArray.push(prod);
    });

    var reEmail =
      selectedREEmail !== "" && selectedREEmail !== "--Bitte auswählen--"
        ? selectedREEmail
        : customEmail4RE;
    var seEmail =
      selectedeSeSEmail !== "" && selectedeSeSEmail !== "--Bitte auswählen--"
        ? selectedeSeSEmail
        : customServicescheinEmail;

    let body = {
      kdnr: ``,
      kommentar: ``,
      startort: ``,
      zielort: ``,
      semail: ``,
      remail: ``,
      automatischeRE: false,
      pos: [] as Product[],
      pdf: "",
    };
    try {
      body = {
        kdnr: `${selectedCustomer.Kundennummer}`,
        kommentar: `${comment}`,
        startort: `${startOrt}`,
        zielort: `${zielOrt}`,
        semail: `${seEmail}`,
        remail: `${reEmail}`,
        automatischeRE: isAutomaticInvoice,
        pos: productsArray,
        pdf: base64pdf,
      };

      await timeout(1000);
    } catch (error) {
      alert("Fehler beim Erstellen des Bodys, bitte Eingaben Überprüfen.");
    }

    if (body.kdnr.length <= 1) {
      alert("Die Kundennummer ist ungültig oder falsch übertragen worden!");
      return setErrorMsg(
        "Fehlerhafte Kundendaten! Bitte überprüfen und erneut versuchen."
      );
    }

    const data = await postServiceschein(body);
    if (data) {
      showDialog({
        title: "Versendet",
        message: "Der Serviceschein Nr." + data + "wurde erfolgreich erstellt.",
        confirmButtonText: "OK",
        onConfirm: () => {},
        cancelButtonText: "",
      });
      setServicescheinSend(true);
      setShowBackButton(false);
    } else {
      setErrorMsg("Fehler beim Senden des Servicescheins!");
      alert("Fehler beim Senden des Servicescheins!");
    }
  };

  const timeout = (delay: number) => {
    return new Promise((res) => setTimeout(res, delay));
  };

  const handleOpenPDF = () => {
    setShowPdfViewer(!showPdfViewer);
    update(
      <PDFServiceschein
        selectedCustomer={selectedCustomer}
        customerSign={customerSignature}
        techSign={technicianSignature}
        vorname={vorname}
        nachname={nachname}
        startTime={startTime}
        endTime={endTime}
        comment={comment}
        products={selectedProducts}
        zielOrt={zielOrt}
        startOrt={startOrt}
        distanz={distanz}
        fahrtZeit={fahrtZeit}
        fahrtBerechnen={fahrtBerechnen}
        day={day}
      />
    );
  };

  const generatePdfDocument = async () => {
    try {
      await update(
        <PDFServiceschein
          selectedCustomer={selectedCustomer}
          customerSign={customerSignature}
          techSign={technicianSignature}
          vorname={vorname}
          nachname={nachname}
          startTime={startTime}
          endTime={endTime}
          comment={comment}
          products={selectedProducts}
          zielOrt={zielOrt}
          startOrt={startOrt}
          distanz={distanz}
          fahrtZeit={fahrtZeit}
          fahrtBerechnen={fahrtBerechnen}
          day={day}
        />
      );

      if (!instance.blob || instance.blob.size <= 0) {
        setErrorMsg("PDF konnte nicht erstellt werden.");
      } else {
        setErrorMsg("PDF erfolgreich erzeugt.");
      }
    } catch (error: any) {
      setErrorMsg("Fehler beim Generieren des PDFs: " + error.message);
    }
  };

  const resetStates = () => {
    setComment("");
    selectedProducts.forEach((p) => {
      p.kommentar = "";
      p.menge = 1;
    });
    setSelectedProducts([]);
    setZielOrt("");
    setStartOrt("");
    setDistanz(0);
    setFahrtZeit(0);
  };

  const handleREEmailChange = (e: string) => {
    setCustomEmail4RE(e);
    setIsREEmailValid(validateEmail(e));
  };

  const handleServicescheinEmailChange = (e: string) => {
    setCustomServicescheinEmail(e);
    setIsServicescheinEmailValid(validateEmail(e));
  };

  const validateEmail = (email: string) => {
    // eslint-disable-next-line no-useless-escape
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w\w+)+$/;
    return emailRegex.test(email);
  };

  const [canSendServiceschein, setCanSendServiceschein] = useState(false);

  const handleCanSendServiceschein = () => {
  
    const isValid = (value: string) =>
      value !== "--Bitte auswählen--" && value !== "";

    const hasValidSeSEmail = isValid(selectedeSeSEmail);
    const hasValidREEmail = isValid(selectedREEmail);
    const hasCustomREEmail = customEmail4RE !== "" && isREEmailValid;
    const hasCustomSeSEmail =
      customServicescheinEmail !== "" && isServicescheinEmailValid;

    if (isAutomaticInvoice) {
      if (hasValidSeSEmail) {
        setCanSendServiceschein(hasValidREEmail || hasCustomREEmail);
      } else if (hasCustomSeSEmail) {
        setCanSendServiceschein(
          hasValidREEmail || (customEmail4RE.length > 1 && isREEmailValid)
        );
      } else {
        setCanSendServiceschein(false);
      }
    } else {
      if (hasValidSeSEmail) {
        setCanSendServiceschein(true);
      } else if (customServicescheinEmail !== "") {
        setCanSendServiceschein(isServicescheinEmailValid);
      }
    }
  };

  useEffect(() => {
    handleCanSendServiceschein();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAutomaticInvoice,
    selectedeSeSEmail,
    customServicescheinEmail,
    isServicescheinEmailValid,
    selectedREEmail,
    customEmail4RE,
    isREEmailValid,
  ]);

  return (
    <>
      <div className="flex justify-center mt-5">
        <div className="w-full max-w-6xl p-4 bg-white shadow-md rounded-lg">
          {/* DefaultMail Eingabe */}
          <div className="flex flex-col items-center">
            <label
              htmlFor="inputAltEmail"
              className="text-gray-700 mb-2 font-semibold"
            >
              Versand-E-Mail für den Serviceschein:
            </label>

            <select
              id="comboBox"
              value={selectedeSeSEmail}
              className="w-full p-2 border rounded-md"
              onChange={(e) => setSelectedeSeSEmail(e.target.value)}
            >
              <option value="--Bitte auswählen--">--Bitte auswählen--</option>
              {selectedCustomer.email.map((email, index) => (
                <option key={index} value={email}>
                  {email}
                </option>
              ))}
            </select>
            {(selectedeSeSEmail === "--Bitte auswählen--" ||
              selectedeSeSEmail === "") && (
              <>
                <input
                  className="w-full mt-5 p-2 border rounded-md"
                  style={{
                    borderColor: isServicescheinEmailValid
                      ? "lightgreen"
                      : "red",
                    borderWidth: "2px",
                  }}
                  type="text"
                  id="inputAltEmail"
                  placeholder="beispiel@bsp.de"
                  value={customServicescheinEmail}
                  onChange={(e) =>
                    handleServicescheinEmailChange(e.target.value)
                  }
                />
              </>
            )}
          </div>
          {/* )} */}

          {/* Automatische Rechnung und Email-Auswahl */}
          {selectedCustomer.Kundennummer.toString() !== "007" ? (
            <>
              {kontrollsumme < 10000 ? (
                <div className="flex items-center justify-between mt-5">
                  <span className="font-semibold">Automatisch Rechnung</span>
                  <ToggleButton
                    isChecked={isAutomaticInvoice}
                    onToggle={() => setisAutomaticInvoice(!isAutomaticInvoice)}
                  />
                </div>
              ) : (
                <div className="mt-5 text-center text-red-600 font-semibold">
                  <h1 className="text-xl">
                    Summe zu hoch für automatische Rechnungserstellung!
                  </h1>
                  <p>({kontrollsumme.toFixed(2)}€)</p>
                </div>
              )}
            </>
          ) : (
            <h1>Testkunde, keine automatische Rechnung möglich!</h1>
          )}

          {isAutomaticInvoice && (
            <div className="mt-5">
              <label
                htmlFor="comboBox"
                className="block text-gray-700 font-semibold mb-2"
              >
                E-Mail Adresse auswählen:
              </label>
              <select
                id="comboBox"
                value={selectedREEmail}
                className="w-full p-2 border rounded-md"
                onChange={(e) => setSelectedREEmail(e.target.value)}
              >
                <option value="--Bitte auswählen--">--Bitte auswählen--</option>
                {selectedCustomer.email.map((email, index) => (
                  <option key={index} value={email}>
                    {email}
                  </option>
                ))}
              </select>

              {(selectedREEmail === "--Bitte auswählen--" ||
                selectedREEmail === "") && (
                <>
                  <label
                    htmlFor="inputField"
                    className="block text-gray-700 mt-4 font-semibold"
                  >
                    Alternativ E-Mail:
                  </label>
                  <input
                    className="w-full p-2 border rounded-md"
                    style={{
                      borderColor: isREEmailValid ? "lightgreen" : "red",
                      borderWidth: "2px",
                    }}
                    type="text"
                    id="inputAltEmail"
                    placeholder="beispiel@bsp.de"
                    value={customEmail4RE}
                    onChange={(e) => handleREEmailChange(e.target.value)}
                  />
                </>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-10 sm:gap-0 sm:justify-between items-center mt-5">
            <button
              className="w-full sm:w-auto bg-gray-600 py-2 px-4 rounded-xl text-white hover:bg-gray-700"
              onClick={handleOpenPDF}
            >
              {showPdfViewer ? "PDF Ausblenden" : "PDF Anzeigen"}
            </button>

            <button
              className="w-full sm:w-auto bg-blue-600 py-2 px-4 rounded-xl text-white hover:bg-gray-700"
              onClick={generatePdfDocument}
            >
              PDF generieren
            </button>

            {canSendServiceschein && (
              <>
                {isLoading ? (
                  <>
                    <Spinner></Spinner>
                  </>
                ) : (
                  <>
                    {servicescheinSend ? (
                      <></>
                    ) : (
                      <CustomButton
                        theme="green"
                        className="w-full sm:w-auto py-2 px-4 rounded-xl text-white"
                        onClick={handleSendServiceSchein}
                      >
                        {loading ? "Versende..." : "Serviceschein versenden"}
                      </CustomButton>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {showPdfViewer && (
            <PDFViewer
              style={{
                width: "100%",
                height: "80vh",
                marginTop: "30px",
                marginBottom: "30px",
              }}
            >
              <PDFServiceschein
                selectedCustomer={selectedCustomer}
                customerSign={customerSignature}
                techSign={technicianSignature}
                vorname={vorname}
                nachname={nachname}
                startTime={startTime}
                endTime={endTime}
                comment={comment}
                products={selectedProducts}
                zielOrt={zielOrt}
                startOrt={startOrt}
                distanz={distanz}
                fahrtZeit={fahrtZeit}
                fahrtBerechnen={fahrtBerechnen}
                day={day}
              />
            </PDFViewer>
          )}

          <input
            className="w-1/2 mt-10 bg-gray-600 py-2 rounded-xl text-white hover:bg-gray-700 cursor-pointer"
            type="submit"
            value={showBackButton ? "Zurück" : "Neuen Serviceschein erstellen"}
            onClick={() => {
              onButtonClick(showBackButton ? "checkpage" : "kundensuche");
              if (!showBackButton) resetStates();
            }}
          />

          {errorMsg && <p className="text-red-500 mt-4">{errorMsg}</p>}
        </div>
      </div>
    </>
  );
};

export default FinalPage;
